import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronRight } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MarkAsOutItem {
  id:                     string
  name:                   string
  emoji:                  string
  last_confirmed_at:      string | null
  avg_restock_cycle_days: number | null
  confirmation_count:     number
}

interface Props {
  item:      MarkAsOutItem
  onClose:   () => void
  onConfirm: (lastBoughtAt: Date | null) => void
}

// ─── Option definitions ───────────────────────────────────────────────────────
//
// The sheet's purpose is cycle-tracking calibration: when the user marks
// something as out, we ask "when did you last buy this?" to refine the
// restock-frequency estimate. It is NOT a "why is this gone" picker.

const OPTIONS = [
  { key: 'recent',  label: 'Recently',          sub: 'Within the last week',           daysAgo: 10 },
  { key: 'while',   label: 'A while ago',       sub: '2–4 weeks ago',                  daysAgo: 21 },
  { key: 'forget',  label: "Can't remember",    sub: "We'll figure it out over time",  daysAgo: 30 },
] as const

// ─── MarkAsOutSheet ───────────────────────────────────────────────────────────

export default function MarkAsOutSheet({ item, onClose, onConfirm }: Props) {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [customDate,     setCustomDate]     = useState('')

  const todayStr     = new Date().toISOString().split('T')[0]
  const sixMonthsAgo = new Date()
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
  const minDateStr = sixMonthsAgo.toISOString().split('T')[0]

  function selectOption(daysAgo: number) {
    const d = new Date()
    d.setDate(d.getDate() - daysAgo)
    onConfirm(d)
  }

  function handleCustomDate() {
    if (!customDate) return
    onConfirm(new Date(`${customDate}T12:00:00`))
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex flex-col justify-end items-center">

        <motion.div
          className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        <motion.div
          className="relative bg-cream rounded-t-[28px] shadow-2xl w-full max-w-[480px] overflow-y-auto"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="h-1 w-10 rounded-full bg-border-avatar" />
          </div>

          {/* Masthead */}
          <div className="flex items-start justify-between px-5 pt-2 pb-5 gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                Mark as out
              </div>
              <h2 className="font-serif text-[22px] font-normal leading-[1.15] tracking-[-0.015em] text-ink">
                When did you last <em className="italic">buy</em> it?
              </h2>
              <p className="font-serif text-[13px] italic text-ink-muted mt-2 leading-[1.45]">
                Helps us learn how often you restock {item.name}.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex-shrink-0 h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
            >
              <X className="h-[14px] w-[14px]" strokeWidth={1.8} />
            </button>
          </div>

          {/* Option buttons */}
          <div className="px-5 flex flex-col gap-2">
            {OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => selectOption(opt.daysAgo)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-[14px] border border-border-sand bg-paper text-left transition-colors active:bg-cream-warm"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[15px] font-medium text-ink leading-tight">
                    {opt.label}
                  </p>
                  <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                    {opt.sub}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-ink-muted flex-shrink-0" strokeWidth={1.5} />
              </button>
            ))}
          </div>

          {/* Custom date — collapsed link → expanded picker */}
          <div className="px-5 pt-4">
            {!showDatePicker ? (
              <button
                type="button"
                onClick={() => setShowDatePicker(true)}
                className="w-full text-center font-serif text-[13px] italic text-ink-muted py-2"
              >
                Pick a specific date
              </button>
            ) : (
              <div className="rounded-[14px] border border-border-sand bg-paper p-3 flex gap-2 items-center">
                <input
                  type="date"
                  value={customDate}
                  min={minDateStr}
                  max={todayStr}
                  onChange={e => setCustomDate(e.target.value)}
                  className="flex-1 rounded-[10px] border border-border-sand bg-cream px-3 py-2 font-serif text-[14px] text-ink focus:outline-none focus:border-forest"
                />
                <button
                  type="button"
                  disabled={!customDate}
                  onClick={handleCustomDate}
                  className="rounded-[10px] bg-forest px-4 py-2 text-[12px] font-bold text-forest-ink disabled:opacity-50"
                >
                  Use this
                </button>
              </div>
            )}
          </div>

          {/* Skip — primary fallback */}
          <div className="px-5 pt-2 pb-8">
            <button
              type="button"
              onClick={() => onConfirm(null)}
              className="w-full py-3 font-serif text-[13px] italic text-ink-muted"
            >
              Skip — just mark it out
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
